<template>
  <div class="min-h-screen bg-gray-50 flex flex-col">
    <AppBar title="Betalen" :show-back-button="true"></AppBar>
    <SpinnerLoader v-if="loading"></SpinnerLoader>
    <template v-if="creditcard">
      <div class="flex-1 overflow-y-auto">
        <div class="max-w-2xl mx-auto px-5 py-5">
          <!-- Order summary card -->
          <div class="bg-white rounded-2xl border border-gray-100 p-4 mb-5">
            <div class="flex items-center gap-3 mb-3">
              <div
                class="flex-shrink-0 w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center"
              >
                <span class="text-xl">&#127915;</span>
              </div>
              <div class="min-w-0 flex-1">
                <h3 class="text-gray-900 text-sm truncate font-medium">
                  {{ isSeasonCard ? 'Seizoenskaart' : 'Strippenkaart' }}
                </h3>
                <span class="text-gray-400 text-xs">{{ creditcard.name }}</span>
              </div>
            </div>
            <p v-if="creditcard.description" class="text-gray-500 text-xs mb-3">
              {{ creditcard.description }}
            </p>
            <div class="border-t border-gray-100 pt-3 flex flex-col gap-1.5">
              <div class="flex items-center justify-between">
                <span class="text-gray-500 text-xs">
                  <template v-if="isSeasonCard">Onbeperkt gebruik</template>
                  <template v-else>
                    {{ creditcard.details && creditcard.details.times_to_use }}x
                    te gebruiken
                  </template>
                </span>
                <span class="text-gray-700 text-xs">
                  {{ $n(creditcard.cost, 'currency') }}
                </span>
              </div>
              <div
                class="flex items-center justify-between pt-2 mt-1 border-t border-dashed border-gray-100"
              >
                <span class="text-gray-700 text-sm">Totaal</span>
                <span class="text-gray-900 font-medium text-base">
                  {{ $n(grandTotal, 'currency') }}
                </span>
              </div>
            </div>
          </div>

          <payment-options
            :step="step"
            :selected-method="selectedMethod"
            :selected-bank="selectedBank"
            :paypal-fee="paypalFee"
            :banks="banks"
            @select-method="handleMethodSelect"
            @select-bank="handleBankSelect"
            @change-method="resetMethod"
          />
        </div>
      </div>

      <!-- Sticky bottom CTA -->
      <div class="flex-shrink-0 bg-white border-t border-gray-100">
        <div class="max-w-2xl mx-auto px-5 pt-3 pb-4">
          <template v-if="canPay">
            <button
              :disabled="paymentLoading"
              class="w-full py-3.5 rounded-full bg-green-600 text-white hover:bg-green-700 active:bg-green-800 transition-colors flex items-center justify-center gap-2"
              @click="startPayment"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <SpinnerLoader v-if="paymentLoading" class="inline-spinner" />
              <span v-else>Betaal {{ $n(grandTotal, 'currency') }}</span>
            </button>
          </template>
          <template v-else>
            <div class="text-center text-gray-400 py-1 text-sm">
              {{
                step === 'method'
                  ? 'Kies een betaalmethode om door te gaan'
                  : 'Kies je bank om door te gaan'
              }}
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import SpinnerLoader from '@/components/SpinnerLoader'
import { mapGetters, mapState } from 'vuex'
import { PAYMENT_GET_METHODS } from '@/store/actions/payment'
import { OPTION_GET } from '@/store/actions/option'
import {
  CREDITCARD_PURCHASE,
  CREDITCARD_GET_ONE
} from '@/store/actions/creditcard'
import PaymentOptions from '@/components/PaymentOptions'
import { IDEAL } from '@/utils/constants'

export default {
  name: 'CreditcardPayment',
  components: { AppBar, SpinnerLoader, PaymentOptions },
  data() {
    return {
      paymentLoading: false,
      cardId: parseInt(this.$route.params.id),
      origin: this.$route.query.origin,
      step: 'method',
      selectedMethod: null,
      selectedBank: null
    }
  },
  computed: {
    ...mapGetters(['authId']),
    ...mapState({
      loading: (state) => state.creditcard.status === 'loading',
      paymentMethods: (state) => state.payment.paymentMethods.data,
      paypalFee: (state) => state.option.options.payments_paypal_fee,
      creditcard: (state) => state.creditcard.selected
    }),
    isSeasonCard: function () {
      return this.creditcard?.type === 'season_card'
    },
    banks: function () {
      return this.paymentMethods?.length
        ? this.paymentMethods.map((issuer) => ({
            key: issuer.bic,
            value: issuer.name,
            image: issuer.image
          }))
        : []
    },
    surcharge: function () {
      if (this.selectedMethod === 'paypal')
        return parseFloat(this.paypalFee) || 0
      return 0
    },
    grandTotal: function () {
      return (parseFloat(this.creditcard?.cost) || 0) + this.surcharge
    },
    canPay: function () {
      return (
        (this.selectedMethod === IDEAL && this.selectedBank !== null) ||
        (this.selectedMethod !== null && this.selectedMethod !== IDEAL)
      )
    }
  },
  watch: {
    creditcard: function (card) {
      if (card && card.active === false) {
        this.$router.replace(`/cards/${this.cardId}`)
      }
    }
  },
  created: function () {
    this.$store.dispatch(PAYMENT_GET_METHODS)
    this.$store.dispatch(OPTION_GET, 'payments_paypal_fee')
    this.$store.dispatch(CREDITCARD_GET_ONE, { creditcardId: this.cardId })
  },
  methods: {
    handleMethodSelect: function (methodId) {
      this.selectedMethod = methodId
      if (methodId === IDEAL) {
        this.step = 'bank'
      }
    },
    handleBankSelect: function (bankKey) {
      this.selectedBank = bankKey
    },
    resetMethod: function () {
      this.step = 'method'
      this.selectedMethod = null
      this.selectedBank = null
    },
    startPayment: async function () {
      this.paymentLoading = true
      const redirectUrl = await this.$store.dispatch(CREDITCARD_PURCHASE, {
        cardId: this.cardId,
        participantId: parseInt(this.authId),
        paymentMethod: this.selectedMethod,
        issuer: this.selectedBank,
        returnUrl: this.origin ? this.origin : null
      })
      if (redirectUrl) {
        window.location.href = redirectUrl
      }
      this.paymentLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

.inline-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
}
</style>
